import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Copy, PaperPlaneTilt, Calendar, Check, Envelope, Clock, User } from "@phosphor-icons/react";

/**
 * Modal "Visita prevista" (lo concierta el gestor).
 *
 * Flujo:
 *   1) Gestor elige FECHA + HORA INICIO + HORA FIN en slots de 15 min.
 *   2) Click "Confirmar visita prevista" → POST /expedientes/:id/cita/notify
 *      con `schedule_in_calendar=true`. El backend:
 *        - Setea `expediente.visita_prevista_at`
 *        - Crea CalendarEvent en la agenda del perito
 *        - Renderiza el texto/email para notificar a la aseguradora
 *   3) Modal muestra resultado (copy o email) + chip "Añadida a la agenda".
 *
 * Props:
 *   - expediente: doc del expediente
 *   - onClose: () => void
 *   - onSaved: () => void  // callback tras éxito (recarga el expediente)
 */
const SLOT_MINUTES = 15;

/** Genera ["08:00","08:15", … "20:45"] cada 15 min entre 07:00 y 22:00. */
function generateTimeSlots() {
  const slots = [];
  for (let h = 7; h <= 22; h++) {
    for (let m = 0; m < 60; m += SLOT_MINUTES) {
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

// Mismas modalidades que el dropdown principal del expediente (DataTab.jsx).
// Si se actualizan ahí, actualizar también aquí.
const TIPOS_ENCARGO = [
  "Presencial",
  "Videoperitación asegurado",
  "Videoperitación reparador",
  "Inspección comunidad",
  "Auditoría",
  "Juicio",
];

function addMinutes(hhmm, delta) {
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + delta;
  const hh = Math.floor(total / 60) % 24;
  const mm = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

export default function VisitaPrevistaModal({ expediente, onClose, onSaved }) {
  // Si ya hay visita_prevista_at, prellenarla
  const prevIso = expediente?.visita_prevista_at;
  const prevFinIso = expediente?.visita_prevista_fin;
  const initialFecha = prevIso ? prevIso.slice(0, 10) : new Date().toISOString().slice(0, 10);
  const initialHoraI = prevIso ? prevIso.slice(11, 16) : "10:00";
  const initialHoraF = prevFinIso ? prevFinIso.slice(11, 16) : addMinutes(initialHoraI, 30);

  const [fecha, setFecha] = useState(initialFecha);
  const [horaInicio, setHoraInicio] = useState(initialHoraI);
  const [horaFin, setHoraFin] = useState(initialHoraF);
  const [modalidad, setModalidad] = useState(expediente?.extracted_data?.tipo_encargo || "");
  const [scheduleInCalendar, setScheduleInCalendar] = useState(true);
  const [notifyAseguradora, setNotifyAseguradora] = useState(true);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [peritoAgenda, setPeritoAgenda] = useState(null);
  const [peritos, setPeritos] = useState([]);
  const [selectedPeritoId, setSelectedPeritoId] = useState("");
  const [assigning, setAssigning] = useState(false);

  // Carga la lista de peritos (para el selector) y resuelve el perito activo
  // del expediente con la misma prioridad que el backend:
  //   perito_asignado_id → extracted_data.perito_codigo
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/peritos");
        if (cancel) return;
        const list = (r.data || []).filter((p) => p.active !== false);
        // Sólo roles que pueden realizar visitas
        const peritosOnly = list.filter((p) =>
          ["perito_interno", "perito_externo", "admin", "gestor_senior"].includes(p.role),
        );
        setPeritos(peritosOnly);
        const id = expediente?.perito_asignado_id;
        const code = (expediente?.extracted_data?.perito_codigo || "").trim();
        let p = id ? peritosOnly.find((x) => x.id === id) : null;
        if (!p && code) p = peritosOnly.find((x) => (x.code || "").toUpperCase() === code.toUpperCase());
        if (p) {
          setPeritoAgenda(p);
          setSelectedPeritoId(p.id);
        }
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.debug("[cita] peritos:", err);
      }
    })();
    return () => { cancel = true; };
  }, [expediente?.id, expediente?.perito_asignado_id, expediente?.extracted_data?.perito_codigo]);

  // Al cambiar hora inicio, ajustar fin si quedó por delante o muy pegado
  useEffect(() => {
    if (horaFin <= horaInicio) {
      setHoraFin(addMinutes(horaInicio, 30));
    }
  }, [horaInicio]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Slots de hora fin filtrados: sólo posteriores a hora inicio
  const finSlots = useMemo(
    () => TIME_SLOTS.filter((t) => t > horaInicio),
    [horaInicio],
  );

  const confirm = async () => {
    if (!selectedPeritoId) {
      toast.error("Selecciona un perito antes de confirmar");
      return;
    }
    setBusy(true); setResult(null); setCopied(false);
    try {
      // Si el perito elegido es distinto al que figura en el expediente,
      // primero asignarlo (extracted_data.perito_codigo) para que quede
      // como perito oficial del encargo.
      const chosen = peritos.find((p) => p.id === selectedPeritoId);
      const currentCode = (expediente?.extracted_data?.perito_codigo || "").toUpperCase();
      if (chosen && chosen.code && chosen.code.toUpperCase() !== currentCode) {
        setAssigning(true);
        try {
          await api.put(`/expedientes/${expediente.id}`, {
            extracted_data: { perito_codigo: chosen.code },
          });
        } finally {
          setAssigning(false);
        }
      }
      const r = await api.post(`/expedientes/${expediente.id}/cita/notify`, {
        fecha, hora_inicio: horaInicio, hora_fin: horaFin, modalidad,
        perito_id: selectedPeritoId,
        schedule_in_calendar: scheduleInCalendar,
      });
      setResult(r.data);
      if (r.data.calendar_event_id) toast.success(`Visita añadida a la agenda de ${chosen?.name || "el perito"}`);
      if (!notifyAseguradora) {
        // El gestor sólo quería agendar, no notificar — cerramos.
        onSaved?.();
        onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la visita");
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const r = await api.post(`/expedientes/${expediente.id}/cita/notify`, {
        fecha, hora_inicio: horaInicio, hora_fin: horaFin, modalidad,
        perito_id: selectedPeritoId,
        send: true, schedule_in_calendar: false,  // ya agendado en el confirm
      });
      setResult(r.data);
      toast.success(`Email enviado a ${r.data.to}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el email");
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(result.body);
      setCopied(true);
      toast.success("Texto copiado");
      setTimeout(() => setCopied(false), 2500);
      onSaved?.();
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.debug("[cita] clipboard:", err);
      toast.error("No se pudo copiar — selecciona el texto manualmente");
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="visita-prevista-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-orange-600 flex items-center gap-1.5">
              <Calendar className="w-3 h-3" weight="bold" />
              Visita prevista
            </div>
            <h2 className="text-lg font-semibold mt-0.5">
              {prevIso ? "Editar visita concertada" : "Concertar visita con el asegurado"}
            </h2>
          </div>
          <button onClick={onClose} className="text-muted hover:text-foreground" data-testid="visita-close">
            <X className="w-5 h-5" weight="bold" />
          </button>
        </header>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Selector de perito al que se asigna la visita */}
          <div className="border border-orange-300 bg-orange-50/40 rounded-md px-3 py-2.5 space-y-1.5" data-testid="visita-perito-banner">
            <label className="text-[10px] uppercase tracking-wider font-bold text-orange-700 flex items-center gap-1.5">
              <User className="w-3 h-3" weight="bold" /> Perito que hará la visita
            </label>
            <select
              value={selectedPeritoId}
              onChange={(e) => setSelectedPeritoId(e.target.value)}
              data-testid="visita-perito-select"
              className="w-full text-sm border border-border rounded-md px-3 py-2 bg-white"
            >
              <option value="">— Selecciona un perito —</option>
              {peritos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code ? `${p.code} — ` : ""}{p.name || p.full_name || p.email}
                </option>
              ))}
            </select>
            {peritoAgenda && selectedPeritoId === peritoAgenda.id ? (
              <div className="text-[11px] text-emerald-800 flex items-center gap-1">
                <Check className="w-3 h-3" weight="bold" />
                Es el perito asignado al expediente. Se agendará en su calendario.
              </div>
            ) : peritoAgenda && selectedPeritoId !== peritoAgenda.id && selectedPeritoId ? (
              <div className="text-[11px] text-amber-800">
                Vas a reasignar este expediente a otro perito. Se actualizará el perito del encargo al confirmar.
              </div>
            ) : !peritoAgenda && selectedPeritoId ? (
              <div className="text-[11px] text-emerald-800 flex items-center gap-1">
                <Check className="w-3 h-3" weight="bold" />
                Este perito quedará asignado al expediente al confirmar.
              </div>
            ) : (
              <div className="text-[11px] text-amber-800">
                Este expediente no tiene perito asignado. Elige uno arriba para agendar la visita en su calendario.
              </div>
            )}
          </div>

          {/* Fecha */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-muted block mb-1">
              <Calendar className="w-3 h-3 inline mr-1" weight="bold" /> Fecha de la visita
            </label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              data-testid="visita-fecha"
              className="w-full text-sm border border-border rounded-md px-3 py-2 bg-white"
            />
          </div>

          {/* Horas en slots de 15 min */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-muted block mb-1">
                <Clock className="w-3 h-3 inline mr-1" weight="bold" /> Hora inicio
              </label>
              <select
                value={horaInicio}
                onChange={(e) => setHoraInicio(e.target.value)}
                data-testid="visita-hora-inicio"
                className="w-full text-sm border border-border rounded-md px-3 py-2 bg-white"
              >
                {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-muted block mb-1">
                <Clock className="w-3 h-3 inline mr-1" weight="bold" /> Hora fin
              </label>
              <select
                value={horaFin}
                onChange={(e) => setHoraFin(e.target.value)}
                data-testid="visita-hora-fin"
                className="w-full text-sm border border-border rounded-md px-3 py-2 bg-white"
              >
                {finSlots.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-muted block mb-1">
              Modalidad (para la notificación)
            </label>
            <select
              value={modalidad}
              onChange={(e) => setModalidad(e.target.value)}
              data-testid="visita-modalidad"
              className="w-full text-sm border border-border rounded-md px-3 py-2 bg-white"
            >
              <option value="">— Sin modalidad —</option>
              {TIPOS_ENCARGO.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
              {/* Si el expediente tiene un tipo_encargo no contemplado en la lista
                  (ej. valor legacy), lo añadimos al final para no perderlo. */}
              {modalidad && !TIPOS_ENCARGO.includes(modalidad) && (
                <option value={modalidad}>{modalidad}</option>
              )}
            </select>
          </div>

          {/* Opciones */}
          <div className="space-y-2">
            <label className="flex items-start gap-2 text-xs cursor-pointer bg-slate-50 border border-border rounded-md px-3 py-2.5">
              <input
                type="checkbox"
                checked={scheduleInCalendar}
                onChange={(e) => setScheduleInCalendar(e.target.checked)}
                className="mt-0.5"
                data-testid="visita-toggle-agenda"
              />
              <div>
                <span className="font-semibold">Añadir a la agenda del perito</span>
                <div className="text-[11px] text-muted mt-0.5">
                  Crea/actualiza un evento en `/calendario` con esta fecha y hora.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-2 text-xs cursor-pointer bg-slate-50 border border-border rounded-md px-3 py-2.5">
              <input
                type="checkbox"
                checked={notifyAseguradora}
                onChange={(e) => setNotifyAseguradora(e.target.checked)}
                className="mt-0.5"
                data-testid="visita-toggle-notificar"
              />
              <div>
                <span className="font-semibold">Generar notificación para la aseguradora</span>
                <div className="text-[11px] text-muted mt-0.5">
                  Muestra el texto a copiar (o envía el email) según la configuración de la compañía.
                </div>
              </div>
            </label>
          </div>

          {!result && (
            <button
              type="button"
              onClick={confirm}
              disabled={busy || assigning || !fecha || !horaInicio || !horaFin || !selectedPeritoId}
              className="btn-brand w-full text-sm py-2.5 rounded-md font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="visita-confirm"
            >
              <Calendar className="w-4 h-4" weight="bold" />
              {busy ? "Guardando..." : assigning ? "Asignando perito..." : "Confirmar visita prevista"}
            </button>
          )}

          {/* Chip — confirmación agendado */}
          {result?.calendar_event_id && (
            <div className="border border-cyan-300 bg-cyan-50 rounded-md px-3 py-2 text-xs text-cyan-900 inline-flex items-center gap-1.5" data-testid="visita-chip-agenda">
              <Calendar className="w-3.5 h-3.5" weight="bold" />
              Añadida a la agenda del perito el <b>{fecha.split("-").reverse().join("/")}</b> de <b>{horaInicio}</b> a <b>{horaFin}</b>
            </div>
          )}

          {/* Notificación: texto a copiar — se muestra SIEMPRE que el toggle
              "Generar notificación" esté activo y haya body, sea cual sea el
              modo configurado en la aseguradora. Si el modo es "email" además
              se muestra debajo el preview con botón "Enviar email". */}
          {result && notifyAseguradora && result.body && result.action !== "email_sent" && (
            <div className="border border-emerald-300 bg-emerald-50 rounded-md p-4 space-y-2" data-testid="visita-copy-result">
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 flex items-center gap-1.5">
                <Copy className="w-3 h-3" weight="bold" />
                Pega este texto en la web de la compañía
              </div>
              <textarea
                readOnly
                value={result.body}
                rows={5}
                className="w-full text-[13px] font-mono border border-emerald-200 rounded-md px-3 py-2 bg-white"
              />
              <button
                type="button"
                onClick={copyToClipboard}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5"
                data-testid="visita-copy-btn"
              >
                {copied ? <Check className="w-3.5 h-3.5" weight="bold" /> : <Copy className="w-3.5 h-3.5" weight="bold" />}
                {copied ? "Copiado" : "Copiar al portapapeles"}
              </button>
              {result.action === "none" && (
                <div className="text-[11px] text-emerald-800/80 pt-1">
                  Texto generado con la plantilla por defecto. Para personalizarlo ve a{" "}
                  <i>Aseguradoras → {expediente?.detected_aseguradora} → Notif. citas</i>.
                </div>
              )}
            </div>
          )}

          {/* Notificación: email enviado */}
          {result?.action === "email_sent" && (
            <div className="border border-emerald-400 bg-emerald-50 rounded-md p-4 flex items-start gap-3" data-testid="visita-email-sent">
              <Check className="w-5 h-5 text-emerald-700 flex-shrink-0" weight="bold" />
              <div>
                <div className="font-bold text-emerald-900">Email enviado a la aseguradora</div>
                <div className="text-xs text-emerald-800 mt-0.5">A: <b>{result.to}</b>{result.cc ? <> · CC: {result.cc}</> : null}</div>
              </div>
            </div>
          )}

          {/* Notificación: preview email + botón Enviar (sólo si la aseguradora
              está configurada en modo "email" y aún no se ha enviado) */}
          {result && notifyAseguradora && result.mode === "email" && result.action !== "email_sent" && (
            <div className="border border-orange-300 bg-orange-50 rounded-md p-4 space-y-3" data-testid="visita-email-preview">
              <div className="text-[10px] uppercase tracking-wider font-bold text-orange-700 flex items-center gap-1.5">
                <Envelope className="w-3 h-3" weight="bold" />
                Esta aseguradora tiene email configurado — puedes enviarlo directamente
              </div>
              <div className="space-y-1 text-[13px]">
                <div><b className="text-muted text-[11px] uppercase">Para:</b> {result.to || <span className="text-rose-600">(sin configurar)</span>}</div>
                {result.cc && <div><b className="text-muted text-[11px] uppercase">CC:</b> {result.cc}</div>}
                <div><b className="text-muted text-[11px] uppercase">Asunto:</b> {result.subject}</div>
              </div>
              <button
                type="button"
                onClick={sendEmail}
                disabled={sending || !result.to}
                className="bg-orange-600 hover:bg-orange-700 text-white text-sm px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="visita-send-btn"
              >
                <PaperPlaneTilt className="w-4 h-4" weight="bold" />
                {sending ? "Enviando..." : "Enviar email"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
